import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  AssetData,
  Scan,
  ScanState,
  SurgeryQuestionnaireInput,
} from 'common/types'
import { useParams } from 'react-router-dom'
import { DispositionData } from 'common/disposition'
import { useEditDocumentedAssets } from 'lib/apollo/hooks'
import {
  EditDocumentedListContextProps,
  EditDocumentedListProviderProps,
  EditDocumentedScanInput,
} from './EditDocumentedList.types'
import { enqueueSnackbar } from 'notistack'

export const EditDocumentedListContext =
  createContext<EditDocumentedListContextProps>({
    scans: [],
    isLoading: false,
    assetGroupId: '',
    scanStateMap: {},
    foundScanRef: { current: undefined },
    isEditModalOpen: false,
    isSelectModalOpen: false,
    openModalIndex: null,
    consumableAssetGroup: undefined,
    setScans: () => {},
    handleReset: () => {},
    scanIsMatching: () => false,
    setScanStateMap: () => {},
    setAssetGroupId: () => {},
    submitDisposition: async () => false,
    setIsEditModalOpen: () => {},
    setOpenModalIndex: () => {},
    isSavingEditedAsset: () => false,
    setIsSelectModalOpen: () => {},
    setConsumableAssetGroup: () => {},
    questionnaire: {
      questions: [],
    },
    setQuestionnaire: () => {},
    triggerFormSubmit: 0,
    setTriggerFormSubmit: () => {},
    triggerSubmit: async () => false,
    triggerSubmitResolverRef: { current: () => {} },
  })

export const EditDocumentedListProvider = ({
  children,
}: EditDocumentedListProviderProps) => {
  const { surgeryId } = useParams()

  const [editDocumentedAssets, editDocumentedAssetsMutation] =
    useEditDocumentedAssets(surgeryId || '')

  // States
  const [scans, setScans] = useState<Scan[]>([])
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isSelectModalOpen, setIsSelectModalOpen] = useState(false)
  const [openModalIndex, setOpenModalIndex] = useState<number | null>(null)
  const [assetGroupId, setAssetGroupId] = useState<AssetData['_id']>('')
  const [consumableAssetGroup, setConsumableAssetGroup] = useState<AssetData>()
  const [scanStateMap, setScanStateMap] = useState<
    Record<Scan['_id'], ScanState>
  >({})
  const [triggerFormSubmit, setTriggerFormSubmit] = useState(0)
  const [questionnaire, setQuestionnaire] = useState<SurgeryQuestionnaireInput>(
    {
      questions: [],
    }
  )
  const foundScanRef = useRef<Scan | undefined>() // used to store the scan that is selected
  const triggerSubmitResolverRef = useRef<(value: boolean) => void>(() => {})

  const triggerSubmit = useCallback((): Promise<boolean> => {
    return new Promise((resolve) => {
      setTriggerFormSubmit((prev) => prev + 1)
      triggerSubmitResolverRef.current = resolve
    })
  }, [])

  // Lifecycle methods
  useEffect(() => {
    foundScanRef.current = scans?.find(
      (scan) => scanStateMap[scan._id]?.isSelected
    )!
  }, [scanStateMap, scans])

  // Handlers
  const handleReset = () => {
    setScanStateMap({})
    setScans([])
    setOpenModalIndex(null)
    foundScanRef.current = undefined
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const submitDisposition = async (
    data: DispositionData,
    scanObject: EditDocumentedScanInput,
    isSponge?: boolean,
    isUnused?: boolean,
    editingScanId?: Scan['_id']
  ): Promise<boolean> => {
    try {
      const result = await editDocumentedAssets({
        variables: {
          surgeryId: surgeryId,
          disposition: data,
          scans: [scanObject],
          isSponge,
          isUnused,
          editingScanId,
        },
      })

      if (result.data?.editDocumentedAssets?.success) {
        return true
      } else {
        enqueueSnackbar('Edit Product Failed. Please try again.', {
          variant: 'error',
        })
        return false
      }
    } catch (error: any) {
      enqueueSnackbar('Edit Product Failed. Please try again.', {
        variant: 'error',
      })
      return false
    }
  }

  const value = useMemo(() => {
    const scanIsMatching = () => {
      return scans.map((scan) => scan._id).includes(foundScanRef.current?._id!)
    }

    const isSavingEditedAsset = (agId: AssetData['_id']) => {
      return editDocumentedAssetsMutation.loading && agId === assetGroupId
    }

    return {
      scans,
      scanStateMap,
      foundScanRef,
      isSelectModalOpen,
      isEditModalOpen,
      openModalIndex,
      isLoading: editDocumentedAssetsMutation.loading,
      assetGroupId,
      consumableAssetGroup,
      setScanStateMap,
      setScans,
      submitDisposition,
      isSavingEditedAsset,
      setIsSelectModalOpen,
      setIsEditModalOpen,
      setOpenModalIndex,
      setAssetGroupId,
      handleReset,
      scanIsMatching,
      setConsumableAssetGroup,
      questionnaire,
      setQuestionnaire,
      triggerFormSubmit,
      setTriggerFormSubmit,
      triggerSubmit,
      triggerSubmitResolverRef,
    }
  }, [
    scanStateMap,
    scans,
    submitDisposition,
    isSelectModalOpen,
    isEditModalOpen,
    openModalIndex,
    editDocumentedAssetsMutation.loading,
    assetGroupId,
    consumableAssetGroup,
    questionnaire,
    triggerFormSubmit,
    setTriggerFormSubmit,
    triggerSubmit,
    triggerSubmitResolverRef,
  ])

  return (
    <EditDocumentedListContext.Provider value={value}>
      {children}
    </EditDocumentedListContext.Provider>
  )
}

export const useEditDocumentedListContext = () => {
  const context = React.useContext(EditDocumentedListContext)
  if (context === undefined) {
    throw new Error(
      'useEditDocumentedListContext must be used within a EditDocumentedListProvider'
    )
  }
  return context
}
